import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Hexagon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import HudFrame from "@/components/primitives/HudFrame";
import { apiCreateCheckoutSession } from "@/lib/api";

/**
 * TierCard — single access tier card.
 * Variants: cyan (Foundation/Architect), cyan-strong (SYSTEM/primary), gold (Genesis).
 */
export default function TierCard({ tier, index }) {
    const [floatY, setFloatY] = useState(0);
    const [shimmerX, setShimmerX] = useState(-100);
    const [busy, setBusy] = useState(false);

    // SYSTEM tier: gentle Y-float
    useEffect(() => {
        if (!tier.primary) return;
        const start = performance.now();
        let frame;
        const tick = (now) => {
            const t = (now - start) / 1000;
            setFloatY(Math.sin(t * 0.7) * 2.5);
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [tier.primary]);

    // Genesis tier: slow gold shimmer sweep
    useEffect(() => {
        if (tier.accent !== "gold") return;
        let i = setInterval(() => {
            setShimmerX(-100);
            requestAnimationFrame(() => {
                setShimmerX(200);
            });
        }, 8000);
        // first sweep
        setTimeout(() => setShimmerX(200), 600);
        return () => clearInterval(i);
    }, [tier.accent]);

    const isPrimary = !!tier.primary;
    const isGold = tier.accent === "gold";

    const accentBorder = isGold
        ? "border-tca-gold/55"
        : isPrimary
        ? "border-tca-cyan/85"
        : "border-tca-cyan/35";

    const headlineGradient = isGold
        ? "gold-brushed"
        : isPrimary
        ? "neon-cyan"
        : "text-tca-ink";

    return (
        <motion.div
            data-testid={`tier-card-${tier.id}`}
            initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{
                duration: 0.7,
                delay: 0.08 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            className={`relative ${isPrimary ? "lg:scale-[1.04] lg:z-10" : ""}`}
            style={{ transform: isPrimary ? `translateY(${floatY}px) scale(1.02)` : undefined }}
        >
            {/* Ribbon */}
            {tier.ribbon && (
                <div
                    data-testid={`tier-ribbon-${tier.id}`}
                    className="
                        absolute -top-3 left-1/2 -translate-x-1/2 z-20
                        font-mono-tight text-[10px] uppercase tracking-[0.32em]
                        text-tca-deep bg-tca-cyan px-4 py-1.5
                    "
                    style={{
                        clipPath: "polygon(6% 0, 94% 0, 100% 100%, 0 100%)",
                        boxShadow: "0 0 22px rgba(0,224,232,0.55)",
                    }}
                >
                    {tier.ribbon}
                </div>
            )}

            <HudFrame
                bracketColor={isGold ? "var(--tca-gold-warm)" : "var(--tca-cyan-core)"}
                bracketSize={14}
                borderTint={isGold ? "rgba(201, 168, 106, 0.20)" : isPrimary ? "rgba(0, 224, 232, 0.22)" : "rgba(0, 224, 232, 0.10)"}
                className={`
                    relative h-full overflow-hidden
                    bg-tca-bg-panel/70 backdrop-blur-xl
                    transition-all duration-500
                    ${isPrimary ? "shadow-glow-cyan-lg" : ""}
                `}
                innerClassName="relative p-6 sm:p-7 lg:p-8 flex flex-col h-full min-h-[480px]"
            >
                {/* Particle field accent (light vs dense) */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none opacity-50"
                    style={{
                        background:
                            isGold
                                ? "radial-gradient(circle at 50% 0%, rgba(224, 192, 137, 0.16), transparent 55%)"
                                : isPrimary
                                ? "radial-gradient(circle at 50% 0%, rgba(0, 224, 232, 0.18), transparent 55%)"
                                : "radial-gradient(circle at 50% 0%, rgba(0, 224, 232, 0.08), transparent 60%)",
                    }}
                />

                {/* Genesis: gold shimmer sweep */}
                {isGold && (
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none overflow-hidden"
                    >
                        <motion.div
                            className="absolute top-0 bottom-0 w-1/3"
                            animate={{ left: `${shimmerX}%` }}
                            transition={{ duration: 2.5, ease: "easeInOut" }}
                            style={{
                                background:
                                    "linear-gradient(110deg, transparent 0%, rgba(224, 192, 137, 0.15) 45%, rgba(255, 240, 200, 0.25) 50%, rgba(224, 192, 137, 0.15) 55%, transparent 100%)",
                                mixBlendMode: "screen",
                            }}
                        />
                    </div>
                )}

                <div className="relative z-10 flex flex-col h-full">
                    {/* Genesis hex emblem */}
                    {isGold && (
                        <div className="flex justify-center mb-4">
                            <div className="relative">
                                <Hexagon
                                    size={32}
                                    strokeWidth={1.4}
                                    className="text-tca-gold-warm"
                                    style={{ filter: "drop-shadow(0 0 12px rgba(224, 192, 137, 0.55))" }}
                                />
                                <Hexagon
                                    size={18}
                                    strokeWidth={1.4}
                                    className="absolute inset-0 m-auto text-tca-gold"
                                />
                            </div>
                        </div>
                    )}

                    {/* Tier name */}
                    <div className={`font-display text-2xl sm:text-3xl tracking-[0.04em] uppercase font-medium ${headlineGradient} text-center`}>
                        {tier.name}
                    </div>

                    {/* Tagline */}
                    <div className={`font-display italic text-sm sm:text-base mt-1.5 text-center ${isGold ? "text-tca-gold-warm/85" : "text-tca-cyan/85"}`}>
                        {tier.tagline}
                    </div>

                    {/* Price */}
                    <div className="mt-7 sm:mt-8 text-center">
                        <div className={`font-mono-tight text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.02em] ${isGold ? "gold-brushed" : "text-tca-ink"}`}>
                            {tier.price}
                        </div>
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted/70 mt-1.5">
                            One-time access
                        </div>
                    </div>

                    {/* Divider */}
                    <div className={`my-7 h-px bg-gradient-to-r from-transparent ${isGold ? "via-tca-gold/40" : "via-tca-cyan/30"} to-transparent`} />

                    {/* Benefits */}
                    <ul className="space-y-3 flex-1">
                        {tier.benefits.map((b, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm leading-snug text-tca-ink-muted">
                                <Check
                                    size={14}
                                    strokeWidth={2.2}
                                    className={`flex-shrink-0 mt-0.5 ${isGold ? "text-tca-gold-warm" : "text-tca-cyan"}`}
                                />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>

                    {/* CTA */}
                    <button
                        type="button"
                        data-testid={`tier-cta-${tier.id}`}
                        disabled={busy}
                        onClick={async () => {
                            if (busy || !tier.sku) return;
                            setBusy(true);
                            try {
                                const { url } = await apiCreateCheckoutSession({ sku: tier.sku });
                                window.location.href = url;
                            } catch (err) {
                                setBusy(false);
                                toast.error(err?.response?.data?.detail || "Checkout failed");
                            }
                        }}
                        className={`
                            group mt-7 sm:mt-8 inline-flex items-center justify-center w-full
                            font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs
                            py-3.5 border backdrop-blur-sm rounded-full
                            transition-all duration-300 ease-out
                            disabled:opacity-60 disabled:cursor-not-allowed
                            ${isGold
                                ? "bg-gradient-to-b from-[rgba(224,192,137,0.18)] to-[rgba(224,192,137,0.05)] border-tca-gold/65 text-tca-gold-warm hover:text-white hover:border-tca-gold-warm hover:shadow-[0_0_28px_rgba(224,192,137,0.45)]"
                                : isPrimary
                                ? "bg-gradient-to-b from-[rgba(0,224,232,0.22)] to-[rgba(0,224,232,0.06)] border-tca-cyan text-tca-cyan-hi hover:text-white shadow-glow-cyan-xs hover:shadow-glow-cyan-lg"
                                : "bg-gradient-to-b from-[rgba(0,224,232,0.14)] to-[rgba(0,224,232,0.04)] border-tca-cyan/65 text-tca-cyan hover:text-tca-cyan-hi hover:border-tca-cyan hover:shadow-glow-cyan-xs"}
                        `}
                    >
                        {busy && <Loader2 size={13} className="mr-2 animate-spin" />}
                        {tier.cta}
                        {!busy && <span className="ml-2 transition-transform group-hover:translate-x-1">→</span>}
                    </button>
                </div>
            </HudFrame>
        </motion.div>
    );
}
